import { Button, Loader, Select, Stack, Text } from '@mantine/core';
import { ArrowLeft, ArrowRight, Package, Plus, Trash } from '@phosphor-icons/react';
import { type FC } from 'react';

import type { AvailableChannel, DistributionPlanSummary } from '@api/distribution';
import { neutralColors, semanticColors } from '@styles/design-system';

const PLAN_STATUS_COLOR: Record<string, string> = {
    DRAFT: neutralColors.textMuted,
    APPROVED: semanticColors.positive.primary,
    EXECUTING: semanticColors.info.primary,
    COMPLETED: semanticColors.positive.primary,
    ARCHIVED: neutralColors.textDisabled,
};

interface Props {
    /** Channels selectable in the "start a new plan" picker (the active sub-tab's set). */
    channels: AvailableChannel[];
    /** Draft plans scoped to the active sub-tab's channels. */
    plans: DistributionPlanSummary[];
    loading: boolean;
    /** Currently-selected channel key in the picker (lifted so it survives navigation). */
    selectedChannelKey: string | null;
    onSelectChannel: (key: string | null) => void;
    onStartPlan: () => void;
    onOpenPlan: (plan: DistributionPlanSummary) => void;
    onDeleteDraft: (plan: DistributionPlanSummary) => void;
    onBack: () => void;
    title?: string;
}

const PlanListView: FC<Props> = ({
    channels, plans, loading, selectedChannelKey, onSelectChannel,
    onStartPlan, onOpenPlan, onDeleteDraft, onBack, title = 'All plans',
}) => (
    <Stack gap={16}>
        <button
            type="button"
            onClick={onBack}
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
                border: 'none', background: 'transparent', cursor: 'pointer',
                fontSize: 13, color: neutralColors.textMuted, padding: 0,
            }}
        >
            <ArrowLeft size={14} /> Back to overview
        </button>

        <Text style={{ fontSize: 16, fontWeight: 600, color: neutralColors.textPrimary }}>{title}</Text>

        {/* New Plan CTA */}
        <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px',
            border: '1px dashed rgba(0,0,0,0.15)', borderRadius: 10,
            backgroundColor: 'rgba(0,0,0,0.01)',
        }}>
            <Select
                placeholder="Select channel…"
                data={channels.map(c => ({ value: c.channelAbbreviationKey, label: c.channelName || c.channelAbbreviationKey }))}
                value={selectedChannelKey}
                onChange={onSelectChannel}
                size="xs"
                style={{ flex: 1 }}
                clearable
                searchable
            />
            <Button
                size="xs"
                leftSection={<Plus size={13} />}
                disabled={!selectedChannelKey}
                onClick={onStartPlan}
            >
                New Plan
            </Button>
        </div>

        {loading ? (
            <Stack align="center" justify="center" style={{ paddingTop: 60 }} gap={8}>
                <Loader size="sm" />
                <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>Loading plans…</Text>
            </Stack>
        ) : plans.length === 0 ? (
            <Stack align="center" justify="center" style={{ paddingTop: 60 }} gap={8}>
                <Package size={32} color={neutralColors.textMuted} />
                <Text style={{ fontSize: 14, color: neutralColors.textMuted }}>
                    No draft plans yet. Select a channel above to start one.
                </Text>
            </Stack>
        ) : (
            <Stack gap={8}>
                {plans.map(plan => {
                    const isDraft = plan.status === 'DRAFT';
                    return (
                        <div
                            key={plan.id}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '12px 16px',
                                border: isDraft ? '1px solid rgba(1,149,255,0.2)' : '1px solid rgba(0,0,0,0.07)',
                                borderRadius: 12,
                                backgroundColor: isDraft ? 'rgba(1,149,255,0.02)' : '#fff',
                                cursor: 'pointer',
                            }}
                            onClick={() => onOpenPlan(plan)}
                        >
                            <div style={{ minWidth: 0 }}>
                                <Text style={{ fontSize: 14, fontWeight: 600, color: neutralColors.textPrimary }}>
                                    {plan.name}
                                </Text>
                                <Text style={{ fontSize: 12, color: neutralColors.textMuted, marginTop: 2 }}>
                                    {new Date(plan.planDate).toLocaleDateString()} ·{' '}
                                    {plan.channelNames.join(', ')} ·{' '}
                                    {plan.lineCount} line{plan.lineCount !== 1 ? 's' : ''}
                                </Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                <span style={{
                                    fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                                    color: PLAN_STATUS_COLOR[plan.status] ?? neutralColors.textMuted,
                                    backgroundColor: `${(PLAN_STATUS_COLOR[plan.status] ?? neutralColors.textMuted)}18`,
                                    borderRadius: 4, padding: '3px 8px',
                                }}>
                                    {plan.status}
                                </span>
                                {isDraft && (
                                    <button
                                        type="button"
                                        title="Delete draft"
                                        onClick={e => { e.stopPropagation(); onDeleteDraft(plan); }}
                                        style={{
                                            border: 'none', background: 'transparent', cursor: 'pointer',
                                            color: semanticColors.critical.primary, padding: 4, borderRadius: 4,
                                            display: 'flex', alignItems: 'center',
                                        }}
                                    >
                                        <Trash size={14} />
                                    </button>
                                )}
                                <ArrowRight size={16} color={neutralColors.textMuted} />
                            </div>
                        </div>
                    );
                })}
            </Stack>
        )}
    </Stack>
);

export default PlanListView;
